import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { getFirestore, collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, query, orderBy, getDoc } from 'firebase/firestore';
import type { Course, Settings } from '../types/course';

const firebaseConfig = {
  apiKey: "AIzaSyD9zK1MVKpXolie2ymqN_uX5GQxWeetybM",
  authDomain: "page-vente-toutes-formations.firebaseapp.com",
  projectId: "page-vente-toutes-formations",
  storageBucket: "page-vente-toutes-formations.firebasestorage.app",
  messagingSenderId: "251435801235",
  appId: "1:251435801235:web:d792de39c92b093f934b66",
  measurementId: "G-ZYPWEHP0TH"
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// Vérifie si l'utilisateur est un admin
async function verifyAdmin() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Utilisateur non authentifié');
  }

  const adminDoc = await getDoc(doc(db, 'admin', 'status'));
  if (!adminDoc.exists()) {
    throw new Error('Configuration admin non trouvée');
  }

  return user;
}

export async function fetchCourses() {
  const coursesCol = collection(db, 'courses');
  const q = query(coursesCol, orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Course[];
}

export async function saveCourse(course: Course) {
  await verifyAdmin();
  
  // Nettoyer les données avant sauvegarde
  const cleanedCourse = { ...course };
  Object.keys(cleanedCourse).forEach(key => {
    if (cleanedCourse[key] === undefined) {
      delete cleanedCourse[key];
    }
  });

  try {
    if (course.id) {
      const docRef = doc(db, 'courses', course.id);
      await updateDoc(docRef, cleanedCourse);
      return cleanedCourse;
    } else {
      const docRef = await addDoc(collection(db, 'courses'), cleanedCourse);
      return { ...cleanedCourse, id: docRef.id };
    }
  } catch (error) {
    console.error('Erreur lors de la sauvegarde du cours:', error);
    throw new Error('Erreur lors de la sauvegarde du cours');
  }
}

export async function updateCourseOrder(courseId: string, newOrder: number) {
  await verifyAdmin();
  const docRef = doc(db, 'courses', courseId);
  await updateDoc(docRef, { order: newOrder });
}

export async function deleteCourse(courseId: string) {
  await verifyAdmin();
  await deleteDoc(doc(db, 'courses', courseId));
}

export async function fetchSettings() {
  const settingsCol = collection(db, 'settings');
  const snapshot = await getDocs(settingsCol);
  return snapshot.docs[0]?.data() as Settings;
}

export async function saveSettings(settings: Settings) {
  await verifyAdmin();
  
  const cleanedSettings = { ...settings };
  Object.keys(cleanedSettings).forEach(key => {
    if (cleanedSettings[key] === undefined) {
      delete cleanedSettings[key];
    }
  });

  try {
    const settingsCol = collection(db, 'settings');
    const snapshot = await getDocs(settingsCol);
    if (snapshot.empty) {
      await addDoc(settingsCol, cleanedSettings);
    } else {
      await updateDoc(snapshot.docs[0].ref, cleanedSettings);
    }
  } catch (error) {
    console.error('Erreur lors de la sauvegarde des paramètres:', error);
    throw new Error('Erreur lors de la sauvegarde des paramètres');
  }
}

export async function createAdminAccount(email: string, password: string) {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await setDoc(doc(db, 'admin', 'status'), {
      exists: true,
      email: email,
      createdAt: new Date().toISOString()
    });
    return userCredential.user;
  } catch (error: any) {
    console.error("Erreur lors de la création du compte admin:", error);
    throw error;
  }
}